import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';
import { UserService } from 'app/services/user.service';
import { TokenRefreshService } from 'app/services/token-refresh.service';

// Phase 7 (EN-3700) — client for the in-portal Agent. The turn endpoint streams Server-Sent Events,
// which Angular's HttpClient cannot consume incrementally, so streamTurn uses fetch() + a stream
// reader and parses the SSE frames itself. EventSource is unusable here: it cannot POST a body and
// cannot set the Authorization header. The bearer token is read explicitly for the same reason
// (the HttpInterceptor that normally injects it only runs on HttpClient requests).

// The only code-execution statuses the backend emits (AgentEvent.CodeExecution). Narrowed to a literal
// union so a typo cannot compile and the UI cannot render an unmapped status.
export type CodeExecStatus = 'running' | 'completed' | 'failed';

// Mirrors the backend SSE frames serialized in AIChatbotController.SerializeEvent.
export type AgentSseEvent =
	| { type: 'text'; text: string }
	| { type: 'tool_call'; name: string; input: string }
	| { type: 'tool_result'; name: string; output: string; isError: boolean }
	| { type: 'usage'; inputTokens: number; outputTokens: number; cacheCreationTokens: number; cacheReadTokens: number }
	// Server-side code-execution progress, for a live status chip.
	| { type: 'code_execution'; status: CodeExecStatus }
	// A file the Agent authored (code-execution output), stored as a short-lived signed download link.
	| { type: 'file'; fileName: string; downloadUrl: string; expiresUtc: string }
	| { type: 'done'; reason: string }
	// Synthesized client-side from the 402 (budget) / other non-OK responses, never sent as SSE.
	| { type: 'cap_hit'; error: string; reason: string }
	| { type: 'error'; status: number; message: string };

export interface AgentTurnAttachment {
	fileName: string;
	// MIME type, e.g. 'application/pdf', 'image/png'.
	mediaType: string;
	// Base64 file bytes (no 'data:...;base64,' prefix).
	data: string;
}

export interface AgentTurnPayload {
	message: string;
	conversationId?: string | null;
	priorTurns?: { role: 'user' | 'assistant'; text: string }[];
	attachments?: AgentTurnAttachment[];
	// Model id chosen in the UI dropdown. Optional; the server resolves/validates it and falls back to
	// the configured default when omitted or unrecognised.
	model?: string;
}

// One selectable Claude model (EN-3700), mirroring the server contract (GET agent/models). Cost is
// computed and frozen server-side per usage row and is intentionally not displayed in the UI
// (Andrew, 2026-06-10); the server stopped sending a per-model rate in EN-4791.
export interface AgentModelOption {
	id: string;
	displayName: string;
}

export interface AgentModelList {
	models: AgentModelOption[];
	defaultModel: string;
}

// EN-4740: the ledger's credit chip (GET agent/credits). Tokens only, never dollars: no client
// surface shows cost (decision 27).
export interface AgentCredits {
	// The client's shared balance, in billable tokens. Null for anyone but a client administrator.
	balance: number | null;

	isAllowed: boolean;
	reason: string;
	message: string | null;

	// Whether a denial is acted on. False during the parity release, when the ledger runs alongside
	// the old cap and nobody is blocked on it.
	enforce: boolean;

	// An Aclarian staff caller burns nothing, so the chip says so rather than showing a countdown.
	isAclarianStaff: boolean;

	// The limit that will bite first, or null when none applies.
	limitCap: number | null;
	limitUsed: number | null;

	// True when the limit is the client's own and this user may not see its totals: the cap and used
	// figures are withheld and percentUsed carries only the warning band (80, 95 or 100).
	limitFiguresHidden?: boolean;
	limitPeriodKind: string | null;
	limitSubjectType: string | null;

	// Null for a Lifetime limit, which renders as "does not reset" rather than a date.
	resetUtc: string | null;

	// The client's zone, which the reset date is shown in. Null when the client has no account.
	timeZoneId: string | null;

	// Whole percent of that limit consumed, or null when no limit applies.
	percentUsed: number | null;
}

// EN-5079 - saved chats. Mirrors AgentSavedChatApiModels.cs. Attachments and files carry names only:
// the bytes were never kept, and a file's download link has expired by the time a chat is reopened.
export type AgentChatRole = 'user' | 'assistant';

export interface AgentSavedChatAttachment {
	fileName: string;
	mediaType: string;
}

export interface AgentSavedChatSummary {
	id: string;
	title: string;
	isPinned: boolean;
	lastMessageUtc: string;
	messageCount: number;
}

export interface AgentSavedChatMessage {
	role: AgentChatRole;
	text: string;
	attachments: AgentSavedChatAttachment[];
	files: string[];
	createdUtc: string;
}

export interface AgentSavedChatDetail extends AgentSavedChatSummary {
	messages: AgentSavedChatMessage[];
}

export interface AgentSavedChatPage {
	items: AgentSavedChatSummary[];
	nextCursor: string | null;
}

export interface AgentSaveChatPayload {
	conversationId: string;
	messages: {
		role: AgentChatRole;
		text: string;
		attachments?: AgentSavedChatAttachment[];
		files?: string[];
	}[];
}

export interface AgentSavedChatUpdate {
	title?: string;
	isPinned?: boolean;
}

// The two warning thresholds, in percent. They live here rather than in the database because a
// stored threshold would need a row per user per window and would still be wrong the moment an
// administrator moved the cap.
export const AGENT_CREDIT_WARNING_PERCENT = 80;
export const AGENT_CREDIT_CRITICAL_PERCENT = 95;

export type AgentCreditWarning = 'none' | 'warning' | 'critical' | 'blocked';

// Which warning a chip should show. Blocked wins over both thresholds: a caller who is already
// refused does not need to be told they are near a limit.
export function agentCreditWarning(credits: AgentCredits | null): AgentCreditWarning {
	if (!credits) return 'none';

	// A refusal the product will ACT ON outranks everything, including staff. The order matters and
	// used to be the other way round: the gate ladder denies at its first three rungs (client not
	// configured, product disabled, not a member, model not allowed) BEFORE it reaches the staff
	// skip, so a decision can carry staff and denied together. Reading staff first painted a genuine
	// refusal as free and threw the reason away, and the easiest way to hit it needs no unusual data
	// at all: the model dropdown offers the server-wide catalog, not the client's allowed list.
	if (!credits.isAllowed && credits.enforce) return 'blocked';

	// Staff burn nothing, so no THRESHOLD applies to them. In practice they carry no limit either,
	// because the staff rung returns before the limit rungs and leaves it null; this is the belt to
	// that braces.
	if (credits.isAclarianStaff) return 'none';

	const percent = credits.percentUsed;
	if (percent === null || percent === undefined) return 'none';

	if (percent >= AGENT_CREDIT_CRITICAL_PERCENT) return 'critical';
	if (percent >= AGENT_CREDIT_WARNING_PERCENT) return 'warning';
	return 'none';
}

@Injectable({
	providedIn: 'root',
})
export class AgentService {
	private readonly basePath = `${environment.apiUrl}aichatbot/clients`;

	constructor(
		private readonly http: HttpClient,
		private readonly userService: UserService,
		private readonly tokenRefreshService: TokenRefreshService
	) {}

	// The models the dropdown may offer plus the default selection. 404 when the feature is off/unconfigured.
	getModels(clientId: string): Observable<AgentModelList> {
		return this.http.get<AgentModelList>(`${this.basePath}/${clientId}/agent/models`);
	}

	// The credit chip. 404 when the feature is off/unconfigured, exactly like getModels, so it never
	// leaks the feature's existence.
	getCredits(clientId: string, model?: string): Observable<AgentCredits> {
		const query = model ? `?model=${encodeURIComponent(model)}` : '';
		return this.http.get<AgentCredits>(`${this.basePath}/${clientId}/agent/credits${query}`);
	}

	// EN-5079 - saved chats. Ordinary HttpClient calls, so the JWT and error interceptors apply. Every
	// call is scoped server-side to the signed-in user; another user's chat reads as a 404.
	saveChat(clientId: string, payload: AgentSaveChatPayload): Observable<AgentSavedChatSummary> {
		return this.http.post<AgentSavedChatSummary>(`${this.basePath}/${clientId}/agent/saved-chats`, payload);
	}

	// A POST, not a GET with ?q=: the search term is the user's own words, and a URL is recorded by the
	// browser monitoring and the server's request logs where a body is not.
	listSavedChats(clientId: string, search: string, cursor: string | null): Observable<AgentSavedChatPage> {
		return this.http.post<AgentSavedChatPage>(`${this.basePath}/${clientId}/agent/saved-chats/query`, {
			search: search || null,
			cursor,
		});
	}

	getSavedChat(clientId: string, chatId: string): Observable<AgentSavedChatDetail> {
		return this.http.get<AgentSavedChatDetail>(
			`${this.basePath}/${clientId}/agent/saved-chats/${encodeURIComponent(chatId)}`
		);
	}

	updateSavedChat(clientId: string, chatId: string, update: AgentSavedChatUpdate): Observable<AgentSavedChatSummary> {
		return this.http.patch<AgentSavedChatSummary>(
			`${this.basePath}/${clientId}/agent/saved-chats/${encodeURIComponent(chatId)}`,
			update
		);
	}

	deleteSavedChat(clientId: string, chatId: string): Observable<void> {
		return this.http.delete<void>(`${this.basePath}/${clientId}/agent/saved-chats/${encodeURIComponent(chatId)}`);
	}

	// Streams one Agent turn. The returned Observable emits typed events as they arrive and completes
	// on the terminal 'done' (or 'cap_hit'/'error') frame. Unsubscribing aborts the in-flight request.
	streamTurn(clientId: string, payload: AgentTurnPayload): Observable<AgentSseEvent> {
		return new Observable<AgentSseEvent>((subscriber) => {
			const controller = new AbortController();

			(async () => {
				// This request bypasses the interceptors, so a 401 here has no refresh path and
				// would simply kill the turn. A stream can also run for minutes, so the token is
				// refreshed before it starts rather than read as-is.
				await this.tokenRefreshService.ensureFreshAsync();
				const token = this.userService.currentUserValue?.token || this.userService.getMsalAccessToken();

				let response: Response;
				try {
					response = await fetch(`${this.basePath}/${clientId}/agent`, {
						method: 'POST',
						signal: controller.signal,
						headers: {
							'Content-Type': 'application/json',
							Accept: 'text/event-stream',
							Authorization: `Bearer ${token}`,
						},
						body: JSON.stringify(payload),
					});
				} catch {
					if (!controller.signal.aborted) {
						subscriber.next({ type: 'error', status: 0, message: 'Could not reach Claire. Please try again.' });
						subscriber.complete();
					}
					return;
				}

				if (response.status === 402) {
					// Budget gate. Render the friendly cap-hit state, not an error.
					const body = await response.json().catch(() => ({ error: '', reason: 'Unknown' }));
					subscriber.next({ type: 'cap_hit', error: body.error, reason: body.reason });
					subscriber.complete();
					return;
				}

				if (response.status === 404) {
					subscriber.next({
						type: 'error',
						status: 404,
						message: 'Claire is not available for your organization.',
					});
					subscriber.complete();
					return;
				}

				if (response.status === 401) {
					subscriber.next({ type: 'error', status: 401, message: 'Your session has expired. Please sign in again.' });
					subscriber.complete();
					return;
				}

				if (response.status === 400) {
					// Validation error (e.g. a rejected attachment). Surface the server's message.
					const body = await response.json().catch(() => ({ error: '' }));
					subscriber.next({
						type: 'error',
						status: 400,
						message: body.error || 'Claire could not start this turn.',
					});
					subscriber.complete();
					return;
				}

				if (!response.ok || !response.body) {
					subscriber.next({
						type: 'error',
						status: response.status,
						message: 'Claire could not start this turn.',
					});
					subscriber.complete();
					return;
				}

				const reader = response.body.getReader();
				const decoder = new TextDecoder();
				let buffer = '';

				try {
					// SSE frames are separated by a blank line; each frame carries one "data: <json>" line.
					for (;;) {
						const { value, done } = await reader.read();
						if (done) break;
						buffer += decoder.decode(value, { stream: true });

						let boundary = buffer.indexOf('\n\n');
						while (boundary !== -1) {
							const frame = buffer.slice(0, boundary);
							buffer = buffer.slice(boundary + 2);
							this.emitFrame(frame, subscriber);
							boundary = buffer.indexOf('\n\n');
						}
					}
					// Flush any trailing frame not terminated by a blank line.
					if (buffer.trim().length > 0) {
						this.emitFrame(buffer, subscriber);
					}
				} catch {
					if (!controller.signal.aborted) {
						subscriber.next({ type: 'error', status: 0, message: 'The connection to Claire was interrupted.' });
					}
				} finally {
					subscriber.complete();
				}
			})();

			return () => controller.abort();
		});
	}

	private emitFrame(frame: string, subscriber: { next: (e: AgentSseEvent) => void }): void {
		for (const line of frame.split('\n')) {
			const trimmed = line.startsWith('data:') ? line.slice(5).trim() : '';
			if (!trimmed) continue;
			try {
				subscriber.next(JSON.parse(trimmed) as AgentSseEvent);
			} catch {
				// A partial/garbled frame is skipped rather than killing the stream.
			}
		}
	}
}
